import classNames from 'classnames';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import Icon from 'components/icons/Icon';
import { IconSymbol, IconVariant } from 'components/icons/Icon.types';
import { ModalDialog } from '../Modal';
import { useModal } from '../ModalContext';

export function useConfirmDelete() {
  const { openModal, closeModal } = useModal();

  return (props: ConfirmDeleteFormProps): Promise<boolean> => {
    return new Promise((resolve) => {
      function handleDelete() {
        if (props.again) {
          closeModal(); // close first modal
          openModal(
            <ConfirmDeleteForm
              prompts={props.again}
              onDelete={() => {
                resolve(true);
                return true;
              }}
              onCancel={() => {
                resolve(false);
                return true;
              }}
            />,
          );
          return false; // keep second modal open
        } else {
          resolve(true);
          return true; // close modal
        }
      }
      openModal(
        <ConfirmDeleteForm
          {...props}
          onDelete={handleDelete}
          onCancel={() => {
            resolve(false);
            return true; // close modal
          }}
        />,
      );
    });
  };
}

type ConfirmDeletePromptProps = {
  title?: ReactNode; // null or undefined for default
  prompt1?: ReactNode; // null or undefined for default
  prompt2?: ReactNode; // null to omit, undefined for default
  serious?: boolean; // adds 'serious' class to pd_alert
};
type ConfirmDeleteFormProps = {
  prompts: ConfirmDeletePromptProps;
  again?: ConfirmDeletePromptProps;
};
type ConfirmDeleteFormHandlerProps = {
  onDelete: () => boolean;
  onCancel?: () => boolean;
};

export function ConfirmDeleteForm(
  props: ConfirmDeleteFormProps & ConfirmDeleteFormHandlerProps,
) {
  const { prompts, onDelete, onCancel } = props;
  const { title, prompt1, prompt2, serious } = prompts;
  return (
    <ModalDialog
      title={title || <FormattedMessage id="GENERAL.DELETE.TITLE" />}
      className={classNames('pd_alert', { serious })}
      okBtn={{
        label: <FormattedMessage id="X.DELETE" />,
        onClick: onDelete,
        isDefault: false,
      }}
      cancelBtn={{
        isDefault: true,
        onClick: onCancel,
      }}
    >
      <>
        <div className="title">
          {prompt1 || <FormattedMessage id="GENERAL.DELETE.OBJECT.CNF" />}
        </div>
        {prompt2 !== null ? (
          <div className="text">
            {prompt2 || <FormattedMessage id="GENERAL.DELETE.OBJECT.CNF2" />}
          </div>
        ) : null}
        <Icon symbol={IconSymbol.alert} variant={IconVariant.accent} />
        <div className="text">
          <FormattedMessage id="GENERAL.DELETE.NOTICE" />
        </div>
      </>
    </ModalDialog>
  );
}
